import React, { useState } from "react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/config";
import { CHECKLIST_SECTIONS } from "./constants";
import { formatDate, formatActivityTimestamp, getChecklistProgress, getEnrollmentType } from "./helpers";

// Internal onboarding checklist — collapsible section inside the Activity Log
// modal. Contractors never see this. Auto items are checked from record state;
// manual items store who/when. Finalization stamps Completed By / Date.
const OnboardingChecklist = ({ submission, currentAdminEmail, showToast, onSubmissionChange }) => {
    const [checklistOpen, setChecklistOpen] = useState(false);
    const [togglingChecklistItem, setTogglingChecklistItem] = useState(null);
    const [checklistNotes, setChecklistNotes] = useState(submission.onboardingChecklist?.notes || "");
    const [savingChecklistNotes, setSavingChecklistNotes] = useState(false);
    const [finalizingChecklist, setFinalizingChecklist] = useState(false);
    const [sendingIntroEmail, setSendingIntroEmail] = useState(false);

    const toggleChecklistItem = async (item) => {
        if (!submission || togglingChecklistItem) return;

        const currentlyChecked = !!submission.onboardingChecklist?.items?.[item.key]?.completed;
        const nowChecked = !currentlyChecked;

        setTogglingChecklistItem(item.key);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);
            const itemData = nowChecked
                ? { completed: true, completedAt: new Date(), completedBy: currentAdminEmail }
                : { completed: false };

            const activityEntry = {
                id: Date.now().toString(),
                type: "checklist",
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                itemKey: item.key,
                itemLabel: item.label,
                checked: nowChecked,
            };

            await updateDoc(docRef, {
                [`onboardingChecklist.items.${item.key}`]: itemData,
                activityLog: arrayUnion(activityEntry),
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    onboardingChecklist: {
                        ...(prev.onboardingChecklist || {}),
                        items: { ...(prev.onboardingChecklist?.items || {}), [item.key]: itemData },
                    },
                    activityLog: [...(prev.activityLog || []), activityEntry],
                };
            });
        } catch (error) {
            console.error("Error updating checklist item:", error);
            showToast("Failed to update checklist. Please try again.", "error");
        } finally {
            setTogglingChecklistItem(null);
        }
    };

    const saveChecklistNotes = async () => {
        if (!submission) return;

        setSavingChecklistNotes(true);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);
            await updateDoc(docRef, {
                "onboardingChecklist.notes": checklistNotes,
                "onboardingChecklist.notesUpdatedAt": new Date(),
                "onboardingChecklist.notesUpdatedBy": currentAdminEmail,
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    onboardingChecklist: {
                        ...(prev.onboardingChecklist || {}),
                        notes: checklistNotes,
                        notesUpdatedAt: new Date(),
                        notesUpdatedBy: currentAdminEmail,
                    },
                };
            });
            showToast("Checklist notes saved");
        } catch (error) {
            console.error("Error saving checklist notes:", error);
            showToast("Failed to save notes. Please try again.", "error");
        } finally {
            setSavingChecklistNotes(false);
        }
    };

    const finalizeChecklist = async (finalize) => {
        if (!submission) return;

        if (finalize) {
            const { done, total } = getChecklistProgress(submission);
            if (done < total) {
                const confirmed = window.confirm(
                    `Only ${done} of ${total} checklist items are complete.\n\nFinalize the onboarding checklist anyway?`
                );
                if (!confirmed) return;
            }
        }

        setFinalizingChecklist(true);
        const recordId = submission.id;
        try {
            const docRef = doc(db, "medical_providers", recordId);
            const finalizedBy = finalize ? currentAdminEmail : null;
            const finalizedAt = finalize ? new Date() : null;

            const activityEntry = {
                id: Date.now().toString(),
                type: "checklist",
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                itemKey: "finalized",
                itemLabel: "Onboarding Checklist",
                checked: finalize,
            };

            await updateDoc(docRef, {
                "onboardingChecklist.finalizedBy": finalizedBy,
                "onboardingChecklist.finalizedAt": finalizedAt,
                activityLog: arrayUnion(activityEntry),
            });

            onSubmissionChange(prev => {
                if (!prev || prev.id !== recordId) return prev;
                return {
                    ...prev,
                    onboardingChecklist: {
                        ...(prev.onboardingChecklist || {}),
                        finalizedBy,
                        finalizedAt,
                    },
                    activityLog: [...(prev.activityLog || []), activityEntry],
                };
            });
            showToast(finalize ? "Onboarding checklist finalized" : "Onboarding checklist reopened");
        } catch (error) {
            console.error("Error finalizing checklist:", error);
            showToast("Failed to update checklist. Please try again.", "error");
        } finally {
            setFinalizingChecklist(false);
        }
    };

    // Send (or resend) the paper-enrollment intro email via admin-only callable
    const handleSendIntroEmail = async () => {
        if (!submission || sendingIntroEmail) return;

        const confirmed = window.confirm(
            `Send the intro email with blank W-9 & Direct Deposit forms to ${submission.email}?`
        );
        if (!confirmed) return;

        setSendingIntroEmail(true);
        try {
            const sendIntro = httpsCallable(functions, "sendPaperIntroEmail");
            await sendIntro({ applicationId: submission.id });
            showToast("Intro email with forms sent");
        } catch (error) {
            console.error("Error sending intro email:", error);
            showToast(`Failed to send intro email: ${error.message}`, "error");
        } finally {
            setSendingIntroEmail(false);
        }
    };

    const progress = getChecklistProgress(submission);
    const finalized = !!submission.onboardingChecklist?.finalizedBy;

    return (
        <div className={`onboarding-checklist ${finalized ? 'finalized' : ''}`}>
            <button
                className="checklist-toggle"
                onClick={() => setChecklistOpen(open => !open)}
            >
                <span className="checklist-caret">{checklistOpen ? "▾" : "▸"}</span>
                <span className="checklist-toggle-title">✅ Onboarding Checklist</span>
                <span className="checklist-toggle-meta">
                    {finalized && <span className="checklist-finalized-badge">Finalized</span>}
                    <span className={`checklist-progress ${progress.done === progress.total ? 'complete' : ''}`}>
                        {progress.done}/{progress.total}
                    </span>
                </span>
            </button>

            {checklistOpen && (
                <div className="checklist-body">
                    <p className="checklist-hint">
                        🔒 Internal use only — contractors never see this checklist. Items marked
                        <span className="checklist-auto-badge inline">Auto</span> are checked automatically
                        when the document or email is on file.
                    </p>

                    {CHECKLIST_SECTIONS.map((section) => (
                        <div key={section.title} className="checklist-section">
                            <h5>{section.title}</h5>
                            {section.items.map((item) => {
                                const stored = submission.onboardingChecklist?.items?.[item.key];
                                const autoChecked = item.auto ? item.auto(submission) : false;
                                const completed = autoChecked || !!stored?.completed;
                                return (
                                    <label
                                        key={item.key}
                                        className={`checklist-item ${completed ? 'done' : ''} ${autoChecked ? 'auto' : ''}`}
                                        title={autoChecked ? item.autoHint : ""}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={completed}
                                            disabled={autoChecked || togglingChecklistItem === item.key}
                                            onChange={() => toggleChecklistItem(item)}
                                        />
                                        <span className="checklist-item-label">{item.label}</span>
                                        {autoChecked && <span className="checklist-auto-badge">Auto</span>}
                                        {!autoChecked && stored?.completed && stored?.completedBy && (
                                            <span className="checklist-item-meta">
                                                {stored.completedBy.split("@")[0]} · {formatActivityTimestamp(stored.completedAt)}
                                            </span>
                                        )}
                                        {item.key === "introEmailSent" && getEnrollmentType(submission) === "paper" && (
                                            <button
                                                type="button"
                                                className="send-intro-btn"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleSendIntroEmail();
                                                }}
                                                disabled={sendingIntroEmail}
                                                title="Send the intro email with blank W-9 & Direct Deposit forms attached"
                                            >
                                                {sendingIntroEmail ? "Sending..." : completed ? "📧 Resend" : "📧 Send Now"}
                                            </button>
                                        )}
                                    </label>
                                );
                            })}
                        </div>
                    ))}

                    <div className="checklist-section checklist-notes">
                        <h5>📝 Notes</h5>
                        <textarea
                            rows="2"
                            value={checklistNotes}
                            onChange={(e) => setChecklistNotes(e.target.value)}
                            placeholder="Internal onboarding notes..."
                        />
                        {checklistNotes !== (submission.onboardingChecklist?.notes || "") && (
                            <button
                                className="save-checklist-notes-btn"
                                onClick={saveChecklistNotes}
                                disabled={savingChecklistNotes}
                            >
                                {savingChecklistNotes ? "Saving..." : "Save Notes"}
                            </button>
                        )}
                    </div>

                    <div className="checklist-footer">
                        {finalized ? (
                            <>
                                <span className="checklist-finalized-text">
                                    ✅ Finalized by {submission.onboardingChecklist.finalizedBy} on {formatDate(submission.onboardingChecklist.finalizedAt)}
                                </span>
                                <button
                                    className="reopen-checklist-btn"
                                    onClick={() => finalizeChecklist(false)}
                                    disabled={finalizingChecklist}
                                >
                                    {finalizingChecklist ? "..." : "Reopen"}
                                </button>
                            </>
                        ) : (
                            <button
                                className="finalize-checklist-btn"
                                onClick={() => finalizeChecklist(true)}
                                disabled={finalizingChecklist}
                            >
                                {finalizingChecklist ? "Finalizing..." : "✔️ Mark Checklist Finalized"}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnboardingChecklist;
